/**
 * Gravity type sizes, restated so they do not depend on the document root.
 *
 * Gravity's spacing, colour and radius tokens are px and therefore stable
 * (`--size-lg: 24px`, `--border-radius: 6px`). Its type is not: every size is
 * `calc(<token> * 1rem)` against an assumed 16px root, so
 * `--font-size-text-sm: 0.875` is meant to render at 14px.
 *
 * This app pins the root to 10px (`shared/styles/antd-global.css`) — around 30
 * files carry rem literals calibrated for that — which renders the same token at
 * 8.75px. The calendar-v2 micro app then re-pins the root to 16px for as long as
 * it is mounted, so an untreated Gravity surface is one size on the calendar
 * route and another everywhere else.
 *
 * `rem` cannot be scoped — it resolves against the root wherever it is used — so
 * these helpers restate the sizes against `16px` directly. Multiplying by
 * `1.6rem` to cancel the 10px root would land the same numbers today, but only
 * while the root stays 10px; `16px` needs no such assumption. The token
 * references are kept either way, so a Gravity version bump still moves these
 * sizes — only the unit differs from what Gravity ships.
 *
 * Retire all of this once the root font-size conflict is resolved; nothing here
 * is wanted on its own merits.
 *
 * ## Weighting a selector that overrides Gravity
 *
 * Any rule built on these helpers must *outweigh* the Gravity rule it replaces,
 * never merely tie with it. Gravity's own selectors are long — `.gv-activated
 * .gv-modal.gv-upgrade-modal .gv-modal-body .gv-product-intro` is (0,5,0) — so a
 * short one is silently overridden.
 *
 * A tie is not enough because source order settles nothing here:
 * `app-manifest/asset-loaders.ts` appends each micro app's stylesheet to `<head>`
 * as that app mounts, long after styled-components has injected. A micro app
 * carrying its own copy of Gravity therefore re-inserts these rules *later* and
 * takes every tie — silently, and only on the routes where it is loaded.
 */

/** The root size Gravity's unitless type tokens are multipliers of. */
const GV_TYPE_BASE = '16px';

/** One Gravity type token, in px. */
export function gravityFontSize(token: string): string {
  return `calc(var(${token}) * ${GV_TYPE_BASE})`;
}

/**
 * Gravity's fluid ramp for a token pair, in px.
 *
 * Gravity ships this as three tiers — flat below 360px, interpolated to 1440px,
 * flat above — which collapses to one `clamp`. Both of the formula's
 * root-relative units are replaced: `* 1rem` becomes `* 16px`, and the `22.5rem`
 * ramp start becomes `360px`. That second substitution matters as much as the
 * first; at a 10px root it would otherwise resolve to 225px and shift where the
 * ramp begins. The 67.5 divisor stays — it is the 1080px ramp width over that
 * same 16px base, and a bare number rather than a length.
 */
export function gravityFluidFontSize(
  minToken: string,
  maxToken: string
): string {
  const min = gravityFontSize(minToken);
  const max = gravityFontSize(maxToken);

  return `clamp(
    ${min},
    calc(${min} + (var(${maxToken}) - var(${minToken})) * ((100vw - 360px) / 67.5)),
    ${max}
  )`;
}
